import { useState } from 'react'
import { ScrollText, RefreshCw, Search, X } from 'lucide-react'
import { getAdminAudit } from '../../services/api'
import {
  C, Card, Row, Btn, Chips, Loading, ErrorLine, Empty, inputStyle,
  useAsync, fmtDateTime, fmtDate, fmtDayKey, fmtTz, userLabel, Pill, divider,
} from './adminUI'

// Раздел «Аудит»: журнал действий юзеров (activity_log).
//
// Раньше здесь было 8 типов событий и плоский список с сырым кодом события в
// pill'е — по такому журналу нельзя было понять, что юзер делал В ПРИЛОЖЕНИИ.
// Задача владельца (2026-09-23): «раздел аудит должен быть более понятным и
// конкретным, а именно я хочу понимать конкретные действия пользователей В
// приложении». Поэтому:
//   • сервер (api.js) теперь пишет действия — боты, ключи, оплаты, поддержка,
//     дневник, рефералка (см. обёртку audit() в TG-BOT/src/api.js);
//   • здесь они раскладываются по категориям, по дням и подписываются
//     человеческим текстом с параметрами («Запустил бота „Scalp1“»).
//
// Время — UTC+3 (см. ADMIN_TZ_OFFSET_MIN в adminUI.jsx): в БД всё в UTC,
// а бизнес-сутки админки считаются по UTC+3, поэтому показывать UTC нельзя.

// ── Категории ───────────────────────────────────────────────────────────────

const CATS = {
  account:  { label: 'Аккаунт',   color: C.accent },
  bots:     { label: 'Боты',      color: C.green },
  keys:     { label: 'Ключи',     color: C.indigo },
  payments: { label: 'Оплата',    color: C.warn },
  settings: { label: 'Настройки', color: C.muted },
  access:   { label: 'Доступ',    color: C.danger },
  support:  { label: 'Поддержка', color: C.indigo },
  referral: { label: 'Рефералы',  color: C.accent },
  other:    { label: 'Прочее',    color: C.muted },
}

// Категория по коду события. Префиксы — те же, что понимает фильтр на сервере
// (adminAuditLog в TG-BOT/src/db.js принимает список LIKE-префиксов).
const CAT_PREFIXES = {
  bots:     ['bot_'],
  keys:     ['keys_'],
  payments: ['pay_', 'stars_invoice'],
  settings: ['set_config', 'diary_'],
  access:   ['admin_block', 'admin_unblock', 'blocked_bot'],
  support:  ['support_'],
  referral: ['referral_'],
  account:  ['registered', 'start', 'set_language', 'set_timezone'],
}

const catOf = (event) => {
  const e = String(event || '')
  for (const [cat, prefixes] of Object.entries(CAT_PREFIXES)) {
    if (prefixes.some(p => e === p || e.startsWith(p))) return cat
  }
  return 'other'
}

// ── Подписи действий ────────────────────────────────────────────────────────
// Коды с параметрами приходят как 'код:параметр1:параметр2'. Старые коды
// (registered, start, set_config, blocked_bot, admin_block, admin_unblock)
// остаются в уже накопленном журнале — их тоже понимаем.

const NET_SHORT = {
  TRC20: 'TRC-20', BEP20: 'BEP-20', SOL_USDT: 'SOL', TON: 'TON', ERC20: 'ERC-20', stars: '⭐ Stars',
}
const MODE_RU = { long: 'лонг', short: 'шорт' }
const ACCOUNT_RU = { real: 'реальный счёт', demo: 'демо-счёт', virtual: 'пробный бот' }

// Простое действие без параметров: код → текст.
const PLAIN = {
  registered:    'Зарегистрировался',
  start:         'Открыл приложение',
  // Батч E (п.4, вариант 2): код set_config пишет меню бота (фильтры,
  // уведомления, лимиты) — подпись уточнена, чтобы не обещать «чёрный список»,
  // которого там нет. Чёрный список из ПРИЛОЖЕНИЯ приходит отдельным кодом
  // set_config_blacklist (префикс тот же — категория «Настройки» и серверный
  // LIKE-фильтр ловят оба без правок структурных карт админки).
  set_config:           'Изменил настройки фильтров',
  set_config_blacklist: 'Изменил чёрный список монет в приложении',
  blocked_bot:   'Заблокировал бота у себя',
  admin_block:   'Заблокирован админом',
  admin_unblock: 'Разблокирован админом',
  bot_start:     'Запустил бота',
  bot_stop:      'Остановил бота',
  bot_delete:    'Удалил бота',
  bot_edit:      'Изменил параметры бота',
  support_send:  'Написал в поддержку',
  diary_sync:    'Синхронизировал дневник',
  diary_clear:   'Очистил дневник',
  referral_bind: 'Привязал реферала',
  pay_confirm:   'Подтвердил оплату',
  pay_cancel:    'Отменил заявку на оплату',
}

const quote = (s) => (s ? ` „${s}“` : '')

// Возвращает { label, cat } — человеческую подпись и категорию.
export function describe(event) {
  const raw = String(event || '')
  if (!raw) return { label: '—', cat: 'other' }
  const [code, ...args] = raw.split(':')
  const cat = catOf(raw)

  if (PLAIN[code]) {
    const base = PLAIN[code]
    // bot_start:Scalp1 → «Запустил бота „Scalp1“»
    if (code.startsWith('bot_') && args[0]) return { label: base + quote(args[0]), cat }
    if (code === 'pay_confirm' && args[0]) return { label: `${base} #${args[0]}`, cat }
    if (code === 'pay_cancel' && args[0]) return { label: `${base} #${args[0]}`, cat }
    // referral_bind:<id пригласившего>:<время> — пишет сам bindReferral в db.js
    // (событие от лица приглашённого, поэтому «пригласивший», а не «пригласил»).
    if (code === 'referral_bind' && args[0]) return { label: `${base} · пригласивший id ${args[0]}`, cat }
    return { label: base, cat }
  }

  switch (code) {
    case 'set_language':
      return { label: `Сменил язык на ${args[0] || '—'}`, cat }
    case 'set_timezone':
      return { label: `Сменил часовой пояс на ${args[0] || '—'}`, cat }
    case 'bot_create': {
      // bot_create:<режим>:<тип счёта>:<имя>
      const mode = MODE_RU[args[0]] || args[0] || ''
      const account = ACCOUNT_RU[args[1]] || args[1] || ''
      const name = args.slice(2).join(':')
      const what = [mode, account].filter(Boolean).join(' · ')
      return { label: `Создал бота${quote(name)}${what ? ` · ${what}` : ''}`, cat }
    }
    case 'keys_connect':
      return { label: `Подключил API-ключи${args[0] ? ` · ${args[0]}` : ''}`, cat }
    case 'keys_delete':
      return { label: `Удалил API-ключи${args[0] ? ` · ${args[0]}` : ''}`, cat }
    case 'pay_create': {
      // pay_create:<тариф>:<сеть>:<сумма>
      const network = NET_SHORT[args[1]] || args[1] || ''
      const sum = args[2] ? `$${args[2]}` : ''
      const what = [args[0], network, sum].filter(Boolean).join(' · ')
      return { label: `Создал заявку на оплату${what ? ` · ${what}` : ''}`, cat }
    }
    case 'stars_invoice': {
      // stars_invoice:<тариф>:<N> ⭐
      const what = [args[0], args[1] ? `${args[1]} ⭐` : ''].filter(Boolean).join(' · ')
      return { label: `Выставил счёт Stars${what ? ` · ${what}` : ''}`, cat }
    }
  }

  return { label: raw, cat }
}

// ── Группировка по дням ─────────────────────────────────────────────────────
// Ключ дня — 'ДД.ММ.ГГГГ' по UTC+3 (тот же сдвиг, что в fmtDate/fmtDayKey),
// чтобы заголовки совпадали с бизнес-сутками статистики, а не с UTC.

const dayKey = (s) => fmtDate(s)

const dayLabel = (dmy, todayKey, yesterdayKey) => {
  if (dmy === todayKey) return 'Сегодня'
  if (dmy === yesterdayKey) return 'Вчера'
  return dmy
}

export default function AuditSection() {
  const [cat, setCat] = useState('')
  const [userInput, setUserInput] = useState('')
  const [userId, setUserId] = useState('')

  // Фильтр по категории уходит на сервер списком префиксов: так в выборку
  // попадают все события категории, а не только те, что влезли в лимит.
  const prefixes = cat ? CAT_PREFIXES[cat] : undefined

  const { data, loading, error, reload } = useAsync(
    () => getAdminAudit({
      limit: 200,
      userId: userId || undefined,
      prefix: prefixes && prefixes.join(','),
    }),
    [cat, userId],
  )

  // «Прочее» — события, не попавшие ни в одну категорию: списком префиксов
  // такое на сервере не выразить, поэтому дофильтровываем на клиенте.
  const rawRows = data?.rows || []
  const rows = cat === 'other' ? rawRows.filter(r => catOf(r.event) === 'other') : rawRows

  const kinds = data?.kinds || []

  // Категории, которые реально есть в журнале (по сводке за всё время).
  const present = new Set(kinds.map(k => catOf(k.event)))
  const catCount = (c) => kinds
    .filter(k => catOf(k.event) === c)
    .reduce((s, k) => s + Number(k.c || 0), 0)
  // Счётчики в чипах — по всему журналу (сервер отдаёт сводку без учёта
  // фильтра по юзеру), поэтому при включённом фильтре их не показываем:
  // иначе цифра на чипе противоречила бы числу записей под ним.
  const chips = [
    { key: '', label: 'Все действия' },
    ...Object.keys(CATS)
      .filter(c => present.has(c))
      .map(c => ({ key: c, label: CATS[c].label, count: userId ? undefined : catCount(c) })),
  ]

  // «Сегодня»/«Вчера» — по календарю админки, а не по часам браузера.
  const nowAdmin = fmtDayKey(Date.now())
  const prevAdmin = fmtDayKey(Date.now() - 86_400_000)

  // Раскладываем по дням, сохраняя порядок (сервер отдаёт по убыванию id).
  const groups = []
  for (const r of rows) {
    const key = dayKey(r.created_at)
    const last = groups[groups.length - 1]
    if (last && last.key === key) last.rows.push(r)
    else groups.push({ key, rows: [r] })
  }

  return (
    <div>
      <div style={{ fontSize: 11, color: C.dim, lineHeight: 1.5, marginBottom: 10 }}>
        Что юзеры делают в приложении: создают и запускают ботов, подключают биржи,
        оплачивают подписку, пишут в поддержку. Время — <b style={{ color: C.muted }}>{fmtTz()}</b>,
        как и во всей остальной админке.
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <input
          value={userInput}
          onChange={e => setUserInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') setUserId(userInput.trim()) }}
          placeholder="id юзера (пусто — все)"
          style={{ ...inputStyle, flex: 1 }}
        />
        {userId ? (
          <Btn icon={X} onClick={() => { setUserId(''); setUserInput('') }}>Сброс</Btn>
        ) : (
          <Btn icon={Search} onClick={() => setUserId(userInput.trim())}>Фильтр</Btn>
        )}
      </div>
      {userId && (
        <div style={{ fontSize: 10.5, color: C.accent, marginBottom: 8 }}>
          Показаны действия только юзера id {userId}
        </div>
      )}

      <Chips options={chips} value={cat} onChange={setCat} />

      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ flex: 1, fontSize: 11.5, color: C.dim }}>
          {loading ? 'Загрузка…' : `Записей: ${rows.length}`}
        </div>
        <Btn small ghost icon={RefreshCw} color={C.muted} onClick={reload}>Обновить</Btn>
      </div>

      <ErrorLine onRetry={reload}>{error}</ErrorLine>

      {loading && !rows.length ? <Loading /> : !rows.length ? <Empty>Действий нет</Empty> : (
        <>
          {groups.map(g => (
            <div key={g.key} style={{ marginBottom: 12 }}>
              <div style={{
                fontSize: 10.5, fontWeight: 700, color: C.muted, letterSpacing: 0.4,
                textTransform: 'uppercase', margin: '0 0 6px 2px',
              }}>
                {dayLabel(g.key, nowAdmin, prevAdmin)}
              </div>
              <Card style={{ padding: '6px 14px' }}>
                {g.rows.map((r, i) => {
                  const d = describe(r.event)
                  const cc = CATS[d.cat] || CATS.other
                  return (
                    <div key={r.id} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      borderTop: i ? `1px solid ${C.line}` : 'none', padding: '9px 0',
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12.5, color: '#fff', overflowWrap: 'anywhere' }}>{d.label}</div>
                        <div style={{ fontSize: 10.5, color: C.dim, marginTop: 2 }}>
                          {userLabel(r)} · id {r.user_id} · {fmtDateTime(r.created_at)}
                        </div>
                      </div>
                      <Pill color={cc.color}>{cc.label}</Pill>
                    </div>
                  )
                })}
              </Card>
            </div>
          ))}
        </>
      )}

      {!!kinds.length && (
        <Card title="Действий за всё время" icon={ScrollText} accent={C.muted} style={{ marginTop: 12 }}>
          {Object.keys(CATS)
            .filter(c => present.has(c) && catCount(c) > 0)
            .map(c => (
              <Row key={c} label={CATS[c].label} value={catCount(c)} valueColor={CATS[c].color} />
            ))}
          <div style={divider} />
          {kinds.slice(0, 10).map(k => (
            <Row
              key={k.event}
              label={<span style={{ fontSize: 11 }}>{describe(k.event).label}</span>}
              value={k.c}
            />
          ))}
          <div style={divider} />
          <div style={{ fontSize: 10.5, color: C.dim, lineHeight: 1.45 }}>
            Журнал ведётся автоматически по действиям в приложении. Одинаковые
            события одного юзера в пределах минуты схлопываются в одну запись.
            Старые записи удаляются по сроку хранения (раздел «Система»).
          </div>
        </Card>
      )}
    </div>
  )
}
